import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  Plus,
  Edit2,
  Trash2,
  Play,
  Ban,
  Mail,
  List as ListIcon,
  Settings,
  Loader2,
  Users,
  CheckCircle,
  XCircle,
} from "lucide-react";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../../components/ConfirmationModal";
import EmailCampaignModal from "../../components/EmailCampaignModal";
import EmailListModal from "../../components/EmailListModal";
import EmailListMembersModal from "../../components/EmailListMembersModal";

const TABS = [
  { id: "campaigns", label: "Campañas", icon: Mail },
  { id: "lists", label: "Listas", icon: ListIcon },
  { id: "settings", label: "Configuración", icon: Settings },
];

const STATUS_LABELS = {
  draft: "Borrador",
  scheduled: "Programada",
  sending: "Enviando",
  sent: "Enviada",
  canceled: "Cancelada",
};

const STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
  scheduled: "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  sending: "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  sent: "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  canceled: "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

const EmailMarketing = () => {
  const [activeTab, setActiveTab] = useState("campaigns");
  const [loading, setLoading] = useState(false);

  const [campaigns, setCampaigns] = useState([]);
  const [campaignStats, setCampaignStats] = useState({});
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const [lists, setLists] = useState([]);
  const [listModalOpen, setListModalOpen] = useState(false);
  const [selectedList, setSelectedList] = useState(null);
  const [membersModalList, setMembersModalList] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [emailConfig, setEmailConfig] = useState({});
  const [savingConfig, setSavingConfig] = useState(false);
  const [testingConfig, setTestingConfig] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testRecipient, setTestRecipient] = useState("");

  // Las listas se cargan siempre, no solo en su pestaña: el modal de campaña
  // necesita el catálogo de listas para el selector de destinatarios aunque el
  // usuario nunca haya entrado a la pestaña "Listas".
  useEffect(() => {
    loadLists();
  }, []);

  useEffect(() => {
    if (activeTab === "campaigns") loadCampaigns();
    if (activeTab === "lists") loadLists();
    if (activeTab === "settings") loadConfig();
  }, [activeTab]);

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/email-marketing/campaigns");
      setCampaigns(data);
      data
        .filter((c) => c.status !== "draft")
        .forEach((c) => loadCampaignStats(c.id));
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const loadCampaignStats = async (campaignId) => {
    try {
      const { data } = await api.get(
        `/email-marketing/campaigns/${campaignId}/stats`
      );
      setCampaignStats((prev) => ({ ...prev, [campaignId]: data }));
    } catch (err) {
      // Silencioso: la barra de progreso simplemente no se pinta.
    }
  };

  const loadLists = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/email-marketing/lists");
      setLists(data);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const loadConfig = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/email-marketing/config");
      setEmailConfig(data);
      setTestResult(null);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const updateConfigField = (key, value) => {
    setEmailConfig((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      const { data } = await api.put("/email-marketing/config", emailConfig);
      setEmailConfig(data);
      toast.success("Configuración guardada");
    } catch (err) {
      toastError(err);
    }
    setSavingConfig(false);
  };

  const handleTestConfig = async () => {
    setTestingConfig(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/email-marketing/config/test", {
        ...emailConfig,
        testRecipient: testRecipient || undefined,
      });
      setTestResult(data);
    } catch (err) {
      toastError(err);
    }
    setTestingConfig(false);
  };

  const handleActivateCampaign = async (campaign) => {
    try {
      await api.post(`/email-marketing/campaigns/${campaign.id}/activate`);
      toast.success("Campaña activada — el envío comenzará en breve");
      loadCampaigns();
    } catch (err) {
      toastError(err);
    }
  };

  const handleCancelCampaign = async (campaign) => {
    try {
      await api.post(`/email-marketing/campaigns/${campaign.id}/cancel`);
      toast.success("Campaña cancelada");
      loadCampaigns();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async () => {
    try {
      if (deleteType === "campaign") {
        await api.delete(`/email-marketing/campaigns/${deleteTarget.id}`);
        toast.success("Campaña eliminada");
        loadCampaigns();
      } else if (deleteType === "list") {
        await api.delete(`/email-marketing/lists/${deleteTarget.id}`);
        toast.success("Lista eliminada");
        loadLists();
      }
    } catch (err) {
      toastError(err);
    }
    setConfirmOpen(false);
    setDeleteTarget(null);
    setDeleteType(null);
  };

  const renderProgressBar = (campaign) => {
    const stats = campaignStats[campaign.id];
    if (!stats || !campaign.totalRecipients) return null;

    const sentPct = (stats.sent / campaign.totalRecipients) * 100;
    const failedPct = (stats.failed / campaign.totalRecipients) * 100;

    return (
      <div className="mt-2">
        <div className="w-full h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden flex">
          <div className="h-full bg-green-500" style={{ width: `${sentPct}%` }} />
          <div className="h-full bg-red-500" style={{ width: `${failedPct}%` }} />
        </div>
        <p className="text-xs text-slate-400 mt-1">
          {stats.sent} enviados · {stats.failed} fallidos · {stats.pending} pendientes
          {stats.skipped > 0 && ` · ${stats.skipped} omitidos`} de {campaign.totalRecipients}
        </p>
      </div>
    );
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deleteTarget
            ? `¿Eliminar "${deleteTarget.name}"?`
            : ""
        }
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleDelete}
      >
        Esta acción no se puede deshacer.
      </ConfirmationModal>

      <EmailCampaignModal
        open={campaignModalOpen}
        onClose={() => {
          setCampaignModalOpen(false);
          setSelectedCampaign(null);
        }}
        campaign={selectedCampaign}
        lists={lists}
        onSave={loadCampaigns}
      />

      <EmailListModal
        open={listModalOpen}
        onClose={() => {
          setListModalOpen(false);
          setSelectedList(null);
        }}
        list={selectedList}
        onSave={loadLists}
      />

      <EmailListMembersModal
        open={!!membersModalList}
        onClose={() => setMembersModalList(null)}
        list={membersModalList}
        onChange={loadLists}
      />

      <MainHeader>
        <Title>Email Marketing</Title>
        <MainHeaderButtonsWrapper />
      </MainHeader>

      <div className="px-6">
        <div className="flex gap-1 border-b border-slate-200 dark:border-slate-800 mb-4">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-indigo-600 text-indigo-600"
                  : "border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 pb-6">
        {activeTab === "campaigns" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-slate-500">
                {campaigns.length} campaña{campaigns.length !== 1 && "s"}
              </p>
              <button
                onClick={() => {
                  setSelectedCampaign(null);
                  setCampaignModalOpen(true);
                }}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
              >
                <Plus className="w-4 h-4" /> Nueva Campaña
              </button>
            </div>

            {loading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
              </div>
            ) : campaigns.length === 0 ? (
              <div className="text-center py-16">
                <Mail className="w-12 h-12 text-slate-200 dark:text-slate-700 mx-auto mb-3" />
                <p className="text-base font-semibold text-slate-500">
                  No hay campañas
                </p>
                <p className="text-sm text-slate-400 mt-1">
                  Crea tu primera campaña de correo
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {campaigns.map((campaign) => (
                  <div
                    key={campaign.id}
                    className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-3"
                  >
                    <div className="flex items-center justify-between">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                            {campaign.name}
                          </p>
                          <span
                            className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_COLORS[campaign.status]}`}
                          >
                            {STATUS_LABELS[campaign.status]}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5 truncate">
                          {campaign.subject} ·{" "}
                          {(campaign.lists || []).map((l) => l.name).join(", ") ||
                            "sin listas"}
                          {campaign.status !== "draft" &&
                            ` · ${campaign.totalRecipients} destinatarios`}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {campaign.status === "draft" && (
                          <>
                            <button
                              onClick={() => handleActivateCampaign(campaign)}
                              title="Activar envío"
                              className="p-1.5 rounded-lg text-green-600 hover:bg-green-50 dark:hover:bg-green-950/30 transition-colors"
                            >
                              <Play className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedCampaign(campaign);
                                setCampaignModalOpen(true);
                              }}
                              title="Editar"
                              className="p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-colors"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setDeleteType("campaign");
                                setDeleteTarget(campaign);
                                setConfirmOpen(true);
                              }}
                              title="Eliminar"
                              className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {(campaign.status === "scheduled" ||
                          campaign.status === "sending") && (
                          <button
                            onClick={() => handleCancelCampaign(campaign)}
                            title="Cancelar envío"
                            className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                          >
                            <Ban className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    {renderProgressBar(campaign)}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "lists" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-slate-500">
                {lists.length} lista{lists.length !== 1 && "s"}
              </p>
              <button
                onClick={() => {
                  setSelectedList(null);
                  setListModalOpen(true);
                }}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
              >
                <Plus className="w-4 h-4" /> Nueva Lista
              </button>
            </div>

            {loading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
              </div>
            ) : lists.length === 0 ? (
              <div className="text-center py-16">
                <ListIcon className="w-12 h-12 text-slate-200 dark:text-slate-700 mx-auto mb-3" />
                <p className="text-base font-semibold text-slate-500">
                  No hay listas
                </p>
                <p className="text-sm text-slate-400 mt-1">
                  Crea una lista para agrupar destinatarios
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {lists.map((list) => (
                  <div
                    key={list.id}
                    className="flex items-center justify-between rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                        {list.name}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5 truncate">
                        {list.memberCount} contacto{list.memberCount !== 1 && "s"}
                        {list.description ? ` · ${list.description}` : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => setMembersModalList(list)}
                        title="Gestionar miembros"
                        className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      >
                        <Users className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedList(list);
                          setListModalOpen(true);
                        }}
                        title="Editar"
                        className="p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setDeleteType("list");
                          setDeleteTarget(list);
                          setConfirmOpen(true);
                        }}
                        title="Eliminar"
                        className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "settings" && (
          <div className="max-w-xl">
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 space-y-4">
              <label className="flex items-center justify-between">
                <span className="text-sm text-slate-700 dark:text-slate-300">
                  Email marketing habilitado
                </span>
                <button
                  type="button"
                  onClick={() => updateConfigField("enabled", !emailConfig.enabled)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${
                    emailConfig.enabled ? "bg-indigo-600" : "bg-slate-300 dark:bg-slate-600"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                      emailConfig.enabled ? "translate-x-5" : ""
                    }`}
                  />
                </button>
              </label>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                    Host SMTP
                  </label>
                  <input
                    type="text"
                    value={emailConfig.smtpHost || ""}
                    onChange={(e) => updateConfigField("smtpHost", e.target.value)}
                    placeholder="smtp.gmail.com"
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                    Puerto
                  </label>
                  <input
                    type="number"
                    value={emailConfig.smtpPort || 587}
                    onChange={(e) =>
                      updateConfigField("smtpPort", Number(e.target.value))
                    }
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div className="flex items-end pb-2.5">
                  <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                    <input
                      type="checkbox"
                      checked={!!emailConfig.smtpSecure}
                      onChange={(e) =>
                        updateConfigField("smtpSecure", e.target.checked)
                      }
                    />
                    Usar TLS (puerto 465)
                  </label>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                    Usuario SMTP
                  </label>
                  <input
                    type="text"
                    value={emailConfig.smtpUser || ""}
                    onChange={(e) => updateConfigField("smtpUser", e.target.value)}
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                    Contraseña SMTP
                  </label>
                  <input
                    type="password"
                    value={emailConfig.smtpPass || ""}
                    onChange={(e) => updateConfigField("smtpPass", e.target.value)}
                    placeholder={
                      emailConfig.smtpPassConfigured
                        ? "•••••••• (ya configurada)"
                        : ""
                    }
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                    Correo remitente
                  </label>
                  <input
                    type="email"
                    value={emailConfig.fromEmail || ""}
                    onChange={(e) => updateConfigField("fromEmail", e.target.value)}
                    placeholder="marketing@tuempresa.com"
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                    Nombre remitente
                  </label>
                  <input
                    type="text"
                    value={emailConfig.fromName || ""}
                    onChange={(e) => updateConfigField("fromName", e.target.value)}
                    placeholder="Tu Empresa"
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                    Responder a (opcional)
                  </label>
                  <input
                    type="email"
                    value={emailConfig.replyToEmail || ""}
                    onChange={(e) =>
                      updateConfigField("replyToEmail", e.target.value)
                    }
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                    Correos por minuto
                  </label>
                  <input
                    type="number"
                    value={emailConfig.sendRatePerMinute || 30}
                    onChange={(e) =>
                      updateConfigField("sendRatePerMinute", Number(e.target.value))
                    }
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                  Pie de baja (HTML, incluye {"{{unsubscribe_link}}"})
                </label>
                <textarea
                  value={emailConfig.unsubscribeFooterText || ""}
                  onChange={(e) =>
                    updateConfigField("unsubscribeFooterText", e.target.value)
                  }
                  rows={3}
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 resize-none font-mono"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Se añade siempre al final de cada correo de campaña — no es opcional.
                </p>
              </div>

              <div className="border-t border-slate-200 dark:border-slate-700 pt-4 space-y-3">
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={testRecipient}
                    onChange={(e) => setTestRecipient(e.target.value)}
                    placeholder="correo para la prueba (opcional)"
                    className="flex-1 px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                  />
                  <button
                    onClick={handleTestConfig}
                    disabled={testingConfig}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 transition-colors"
                  >
                    {testingConfig && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    Probar conexión
                  </button>
                </div>
                {testResult && (
                  <div
                    className={`flex items-start gap-2 text-sm px-3 py-2 rounded-lg ${
                      testResult.success
                        ? "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                        : "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                    }`}
                  >
                    {testResult.success ? (
                      <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    )}
                    <span>{testResult.message}</span>
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-2">
                <button
                  onClick={handleSaveConfig}
                  disabled={savingConfig}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  {savingConfig && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Guardar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainContainer>
  );
};

export default EmailMarketing;
